/**
 * Minimal embedded-Superset host — replaces @superset-ui/embedded-sdk.
 *
 * Why not the SDK (0.4.0): it force-adds the `allow-presentation` sandbox
 * flag (Presentation-API casting), which Safari doesn't implement and logs a
 * console error for on every mount; and it swallows refresh-mint failures
 * inside a silent 10-second retry loop with no error surface — the old
 * SupersetDashboard carried a 20-line workaround wrapping fetchGuestToken
 * for exactly that hole. This module implements the same wire contract the
 * SDK does (Superset's own embedded page speaks it):
 *
 *   1. iframe src = `${supersetDomain}/embedded/${uuid}?uiConfig=N`
 *      (uiConfig is a bitfield; bit 1 = hideTitle).
 *   2. On iframe `load`: create one MessageChannel and transfer port2 into
 *      the iframe with postMessage({type:'__embedded_comms__',
 *      handshake:'port transfer'}).
 *   3. Send {switchboardAction:'emit', method:'guestToken',
 *      args:{guestToken}} on the retained port1, and re-send shortly before
 *      each token's `exp`.
 *
 * Failure contract: real token or onError — never a silent retry. A failed
 * refresh mint surfaces immediately through onError and the refresh cycle
 * stops; retry is the caller's (the user's) move.
 */

import { decodeJwtPayload } from '@/lib/authIdentity';

export interface SupersetEmbedOptions {
  /** EmbeddedDashboard uuid (from GET /analytics/dashboards). */
  uuid: string;
  /** Browser-facing Superset origin, e.g. https://superset-embed.staging.tricorder.icu */
  supersetDomain: string;
  mountPoint: HTMLElement;
  /** Mints a fresh guest token; re-invoked before every token expiry. */
  fetchGuestToken: () => Promise<string>;
  /** Fires when a post-mount refresh mint fails (initial failures reject mount()). */
  onError: (error: unknown) => void;
  hideTitle?: boolean;
}

export interface SupersetEmbedHandle {
  unmount: () => void;
}

/** Message type Superset's embedded page listens for to receive its comms port. */
const IFRAME_COMMS_MESSAGE_TYPE = '__embedded_comms__';

/**
 * The SDK's sandbox set minus `allow-presentation` (Presentation-API casting
 * — unimplemented in Safari, which logs a console error per mount token; an
 * admin dashboard is never cast). Fullscreen is governed by the `allow`
 * attribute below, not a sandbox flag, so nothing else is lost.
 */
const SANDBOX_FLAGS = [
  'allow-same-origin', // postMessage handshake
  'allow-scripts',
  'allow-downloads', // chart image export
  'allow-forms',
  'allow-popups', // CSV export
] as const;
const ALLOW_FEATURES = 'fullscreen; clipboard-write';

/**
 * Protocol constants mirroring the SDK's refresh timing (guest tokens live
 * ~5 min server-side): re-mint REFRESH_BUFFER_MS before `exp`, floored at
 * MIN_REFRESH_WAIT_MS so a skewed clock (exp already "past" at mint time)
 * degrades to a slow cycle instead of a hot mint loop.
 */
const REFRESH_BUFFER_MS = 5_000;
const MIN_REFRESH_WAIT_MS = 10_000;

/**
 * `exp` (ms since epoch) from a JWT. Throws on an unreadable token: guest
 * tokens are minted by our own backend and are always standard JWTs, so an
 * undecodable one is an invariant violation to surface, not to paper over
 * with a guessed refresh interval.
 */
function tokenExpiryMs(token: string): number {
  const decoded = decodeJwtPayload<{ exp: number }>(token);
  if (typeof decoded.exp !== 'number') {
    throw new Error('guest token carries no numeric exp claim');
  }
  return decoded.exp * 1000;
}

/**
 * Mounts the embedded dashboard into `mountPoint` and starts the guest-token
 * refresh cycle. Resolves once the iframe is up and the first token has been
 * delivered; rejects if the first mint or the handshake fails.
 */
export async function embedSupersetDashboard(
  options: SupersetEmbedOptions,
): Promise<SupersetEmbedHandle> {
  const { uuid, mountPoint, fetchGuestToken, onError, hideTitle } = options;
  const supersetDomain = options.supersetDomain.replace(/\/$/, '');

  let unmounted = false;
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;

  const mountIframe = new Promise<MessagePort>((resolve) => {
    const iframe = document.createElement('iframe');
    for (const flag of SANDBOX_FLAGS) {
      iframe.sandbox.add(flag);
    }
    iframe.setAttribute('allow', ALLOW_FEATURES);
    iframe.style.background = 'transparent';
    iframe.addEventListener('load', () => {
      const channel = new MessageChannel();
      // contentWindow is never null inside a load handler; the optional
      // chain only satisfies the DOM lib's nullable type.
      iframe.contentWindow?.postMessage(
        { type: IFRAME_COMMS_MESSAGE_TYPE, handshake: 'port transfer' },
        supersetDomain,
        [channel.port2],
      );
      resolve(channel.port1);
    });
    const uiConfig = hideTitle ? 1 : 0;
    iframe.src = `${supersetDomain}/embedded/${uuid}?uiConfig=${uiConfig}`;
    mountPoint.replaceChildren(iframe);
  });

  let firstToken: string;
  let port: MessagePort;
  try {
    [firstToken, port] = await Promise.all([fetchGuestToken(), mountIframe]);
  } catch (error) {
    mountPoint.replaceChildren();
    throw error;
  }

  function sendToken(guestToken: string): void {
    port.postMessage({
      switchboardAction: 'emit',
      method: 'guestToken',
      args: { guestToken },
    });
  }

  function scheduleRefresh(currentToken: string): void {
    const wait = Math.max(
      tokenExpiryMs(currentToken) - Date.now() - REFRESH_BUFFER_MS,
      MIN_REFRESH_WAIT_MS,
    );
    refreshTimer = setTimeout(() => {
      fetchGuestToken()
        .then((nextToken) => {
          if (unmounted) return;
          sendToken(nextToken);
          scheduleRefresh(nextToken);
        })
        // .catch (not a rejection handler on the .then) so a tokenExpiryMs
        // invariant throw from scheduleRefresh lands here too, not as an
        // unhandled rejection. Real token or an explicit error — the cycle
        // stops and the caller decides what a retry means (for the admin
        // dashboard: an error banner with a user-initiated remount).
        .catch((error: unknown) => {
          if (!unmounted) onError(error);
        });
    }, wait);
  }

  sendToken(firstToken);
  try {
    scheduleRefresh(firstToken);
  } catch (error) {
    // tokenExpiryMs's fail-loud invariant throw, after the iframe is
    // already live: tear the mount down and reject through the same
    // mount-failure path as any other initial error — never an orphaned
    // iframe/port behind an error banner.
    port.close();
    mountPoint.replaceChildren();
    throw error;
  }

  return {
    unmount: () => {
      unmounted = true;
      if (refreshTimer !== undefined) clearTimeout(refreshTimer);
      port.close();
      mountPoint.replaceChildren();
    },
  };
}
